import React from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';

import { Div, Icon, Tag, Text } from '@pinpoint/design-kit';

import Rbac from 'src/containers/rbac';
import { formatNumber, formatPercent, getModelTagStyles } from 'src/functions';
import { ROUTES } from 'src/config';
import CONSTANTS from 'src/store/constants';
import ActuarialDownloads from './actuarial_downloads';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

/* eslint-disable react/prop-types */
export function CellActions({
  id,
  recentStatus,
  siteRole,
  hiddenByRole,
  setModelToApproveId,
  setModelToDeleteId,
  setModelToHideId,
  setHideModalOpen,
  setApproveModalOpen,
  setDeleteModalOpen,
  checkDeleteModel,
}) {
  return (
    <>
      {recentStatus === CONSTANTS.TAL_MODEL.STATE.PENDING_APPROVAL ? (
        <Rbac
          roles={[CONSTANTS.ROLE.SUPER_ADMIN, CONSTANTS.ROLE.CLIENT_SERVICES_ADMIN, CONSTANTS.ROLE.CLIENT_SERVICES]}
        >
          <Icon
            name="Checked"
            cursor="pointer"
            hoverTransform="scale(1.25)"
            color="lightGrey"
            hoverColor="brand500"
            size=".75rem"
            m={{ r: '0.3125rem' }}
            data-test="approve"
            data-tip="Approve model"
            onClick={() => {
              setModelToApproveId(id);
              setApproveModalOpen(true);
            }}
          />
        </Rbac>
      ) : null}
      <Rbac
        roles={[CONSTANTS.ROLE.SUPER_ADMIN, CONSTANTS.ROLE.CLIENT_SERVICES_ADMIN]}
        fallback={
          siteRole === CONSTANTS.ROLE.CLIENT_SERVICES ? (
            <Icon
              name={hiddenByRole ? 'ClosedEye' : 'Visualize'}
              viewBox={hiddenByRole ? '3 33 66 29' : undefined}
              data-tip={hiddenByRole ? 'Model is hidden' : 'Model is visible'}
              color="lightGrey"
              size=".75rem"
              m={{ r: '0.3125rem' }}
              data-test="hide"
            />
          ) : null
        }
      >
        <Icon
          name={hiddenByRole ? 'ClosedEye' : 'Visualize'}
          viewBox={hiddenByRole ? '3 33 66 29' : undefined}
          data-tip={hiddenByRole ? 'Model is hidden' : 'Model is visible'}
          cursor="pointer"
          hoverTransform="scale(1.25)"
          color="lightGrey"
          hoverColor="brand500"
          size=".75rem"
          m={{ r: '0.3125rem' }}
          data-test="hide"
          onClick={() => {
            setModelToHideId(id);
            setHideModalOpen(true);
          }}
        />
      </Rbac>
      <Rbac
        roles={[CONSTANTS.ROLE.ADMIN, CONSTANTS.ROLE.SUPER_ADMIN]}
        fallback={<Icon color="tabRest" name="Delete" cursor="not-allowed" size=".75rem" m={{ x: '0' }} />}
      >
        <Icon
          name="Delete"
          cursor="pointer"
          hoverTransform="scale(1.25)"
          size=".75rem"
          color="lightGrey"
          hoverColor="danger800"
          m={{ x: '0' }}
          data-test="delete"
          data-tip="Delete model"
          onClick={() => {
            setModelToDeleteId(id);
            checkDeleteModel(id);
            setDeleteModalOpen(true);
          }}
        />
      </Rbac>
    </>
  );
}

export function ActuarialCellActions({
  id,
  dataModelType,
  recentStatus,
  hiddenByRole,
  metadata,
  data,
  validation,
  role,
  siteRole,
  originalTenant,
  setModelToApproveId,
  setModelToDeleteId,
  setModelToDeleteType,
  setModelToHideId,
  setHideModalOpen,
  setDeleteModalOpen,
  checkDeleteModel,
  setApproveModalOpen,
  fetchEntityDl,
  fetchModelDl,
}) {
  return (
    <>
      {recentStatus === CONSTANTS.TAL_MODEL.STATE.PENDING_APPROVAL &&
      dataModelType !== CONSTANTS.TAL_MODEL.TAG.ACTUARIAL ? (
        <Rbac
          roles={[CONSTANTS.ROLE.SUPER_ADMIN, CONSTANTS.ROLE.CLIENT_SERVICES_ADMIN, CONSTANTS.ROLE.CLIENT_SERVICES]}
        >
          <Icon
            name="Checked"
            cursor="pointer"
            hoverTransform="scale(1.25)"
            color="lightGrey"
            hoverColor="brand500"
            size=".75rem"
            m={{ r: '0.3125rem' }}
            data-test="approve"
            data-tip="Approve model"
            onClick={() => {
              setModelToApproveId(id);
              setApproveModalOpen(true);
            }}
          />
        </Rbac>
      ) : null}
      <Rbac
        roles={[CONSTANTS.ROLE.SUPER_ADMIN, CONSTANTS.ROLE.CLIENT_SERVICES_ADMIN]}
        fallback={
          siteRole === CONSTANTS.ROLE.CLIENT_SERVICES ? (
            <Icon
              name={hiddenByRole ? 'ClosedEye' : 'Visualize'}
              viewBox={hiddenByRole ? '3 33 66 29' : undefined}
              data-tip={hiddenByRole ? 'Model is hidden' : 'Model is visible'}
              color="lightGrey"
              size=".75rem"
              m={{ r: '0.3125rem' }}
              data-test="hide"
            />
          ) : null
        }
      >
        <Icon
          name={hiddenByRole ? 'ClosedEye' : 'Visualize'}
          cursor="pointer"
          viewBox={hiddenByRole ? '3 33 66 29' : undefined}
          data-tip={hiddenByRole ? 'Model is hidden' : 'Model is visible'}
          hoverTransform="scale(1.25)"
          color="lightGrey"
          hoverColor="brand500"
          size=".75rem"
          m={{ r: '0.3125rem' }}
          data-test="hide"
          onClick={() => {
            setModelToHideId(id);
            setHideModalOpen(true);
          }}
        />
      </Rbac>
      <Rbac
        roles={[CONSTANTS.ROLE.ADMIN, CONSTANTS.ROLE.SUPER_ADMIN]}
        fallback={<Icon color="tabRest" name="Delete" cursor="not-allowed" size=".75rem" m={{ r: '0.3125rem' }} />}
      >
        <Icon
          name="Delete"
          cursor="pointer"
          hoverTransform="scale(1.25)"
          size=".75rem"
          color="lightGrey"
          hoverColor="danger800"
          m={{ r: '0.3125rem' }}
          data-test="delete"
          data-tip="Delete model"
          onClick={() => {
            setModelToDeleteId(id);
            setModelToDeleteType(dataModelType);
            checkDeleteModel(id);
            setDeleteModalOpen(true);
          }}
        />
      </Rbac>
      {recentStatus === CONSTANTS.TAL_MODEL.STATE.LIFT_CHART_GENERATION_SUCCESS ? (
        <Rbac roles={[CONSTANTS.ROLE.SUPER_ADMIN]}>
          <ActuarialDownloads
            id={id}
            modelMetadata={metadata}
            modelData={data}
            validationCluster={validation}
            dataModelType={dataModelType}
            iconSize=".75rem"
            originalTenant={originalTenant}
            role={role}
            fetchEntityDl={fetchEntityDl}
            fetchModelDl={fetchModelDl}
          />
        </Rbac>
      ) : null}
    </>
  );
}

export function CellDate({ value, ...props }) {
  return (
    <Text
      textWeight="400"
      textSize="small"
      data-tip={dayjs(value).format('HH:mm [GMT]Z (zzz) — ddd, MMM D, YYYY')}
      {...props}
    >
      {dayjs(value).format('MMM DD, YYYY')}
    </Text>
  );
}

export function CellStatus({ value }) {
  switch (value) {
    case 1:
      return <Div rounded="sm" bg="marigold" h=".9rem" w=".9rem" data-tip="Pending approval" />;
    case 2:
      return <Div rounded="sm" bg="blueLight" h=".9rem" w=".9rem" data-tip="In progress" />;
    case 3:
      return <Div rounded="sm" bg="vividRed" h=".9rem" w=".9rem" data-tip="Errored" />;
    case 4:
      return <Div rounded="sm" bg="rioGrande" h=".9rem" w=".9rem" data-tip="Completed" />;
    case 5:
      return (
        <Div
          rounded="sm"
          bg="talUnknown"
          h=".9rem"
          w=".9rem"
          data-tip="Pending completion of frequency and severity models"
        />
      );
    default:
      return <Div rounded="sm" bg="talUnknown" h=".9rem" w=".9rem" />;
  }
}

export function CellPercent({ value, ...props }) {
  return (
    <Text textWeight="400" textSize="small" {...props} data-tip={formatPercent(value)}>
      {formatPercent(value)}
    </Text>
  );
}

export function CellText({ value, ...props }) {
  return (
    <Text textWeight="400" textSize="small" {...props}>
      {value}
    </Text>
  );
}

export function CellNumber({ value, ...props }) {
  return (
    <Text textWeight="400" textSize="small" {...props} data-tip={formatNumber(value)}>
      {formatNumber(value)}
    </Text>
  );
}

export function CellHit({ modelData }) {
  const { dataPayload } = modelData;
  if (dataPayload?.positiveSegment?.filter && dataPayload?.positiveSegment?.dataset) {
    const { positiveSegment } = dataPayload;
    const { filter, dataset, name } = positiveSegment;
    return (
      <Text
        m={{ r: '0.25rem' }}
        data-tip={filter === 'POPULATION_SNAPSHOT' ? dataset.name : name}
        maxW="8rem"
        textWeight="400"
        textSize="small"
        align="center"
        style={{
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        {filter === 'POPULATION_SNAPSHOT' ? dataset.name : name}
      </Text>
    );
  }
  return null;
}

export function CellHitMatched({ row }) {
  const { dataPayload } = row.original.modelData;
  if (dataPayload?.positiveSegment?.id && dataPayload?.positiveSegment?.matched) {
    const { positiveSegment } = dataPayload;
    const { id, matched } = positiveSegment;
    return (
      <Div d="flex" align="center" justify="space-between" w="100%">
        <Text
          m={{ x: '0.25rem' }}
          data-tip={formatNumber(matched)}
          maxW="14rem"
          textWeight="400"
          textSize="small"
          style={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {formatNumber(matched)}
        </Text>
        <Link
          to={ROUTES.VIEW_SEGMENTS.replace(':entity', 'segment').replace(':entityId', id)}
          target="_blank"
          data-tip="Visualize in new tab"
        >
          <Icon name="NewWindow2" hoverTransform="scale(1.25)" size=".75rem" color="lightGrey" viewBox="0 0 12 12" />
        </Link>
      </Div>
    );
  }
  return null;
}

export function CellMiss({ modelData }) {
  const { dataPayload } = modelData;
  if (dataPayload?.negativeSegment?.filter && dataPayload?.negativeSegment?.dataset) {
    const { negativeSegment } = dataPayload;
    const { filter, dataset, name } = negativeSegment;
    return (
      <Div d="flex" align="center" justify="space-between" w="100%" m={{ l: '2rem' }}>
        <Text
          m={{ r: '0.25rem' }}
          data-tip={filter === 'POPULATION_SNAPSHOT' ? dataset.name : name}
          maxW="8rem"
          textWeight="400"
          textSize="small"
          style={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {filter === 'POPULATION_SNAPSHOT' ? dataset.name : name}
        </Text>
      </Div>
    );
  }
  return null;
}

export function CellMissMatched({ row }) {
  const { dataPayload } = row.original.modelData;
  if (dataPayload?.negativeSegment?.id && dataPayload?.negativeSegment?.matched) {
    const { negativeSegment } = dataPayload;
    const { id, matched } = negativeSegment;
    return (
      <Div d="flex" align="center" justify="space-between" w="100%">
        <Text
          m={{ x: '0.25rem' }}
          data-tip={formatNumber(matched)}
          maxW="14rem"
          textWeight="400"
          textSize="small"
          style={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {formatNumber(matched)}
        </Text>
        <Link
          to={ROUTES.VIEW_SEGMENTS.replace(':entity', 'segment').replace(':entityId', id)}
          target="_blank"
          data-tip="Visualize in new tab"
        >
          <Icon name="NewWindow2" hoverTransform="scale(1.25)" size=".75rem" color="lightGrey" viewBox="0 0 12 12" />
        </Link>
      </Div>
    );
  }
  return null;
}

export function CellValidation({ modelData }) {
  const { dataPayload } = modelData;
  if (dataPayload?.validationSegment?.filter && dataPayload?.validationSegment?.dataset) {
    const { validationSegment } = dataPayload;
    const { filter, dataset, name } = validationSegment;
    return (
      <Div d="flex" align="center" justify="space-between" w="100%" m={{ l: '2rem' }}>
        <Text
          m={{ r: '0.25rem' }}
          data-tip={filter === 'POPULATION_SNAPSHOT' ? dataset.name : name}
          maxW="8rem"
          textWeight="400"
          textSize="small"
          style={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {filter === 'POPULATION_SNAPSHOT' ? dataset.name : name}
        </Text>
      </Div>
    );
  }
  return null;
}

export function CellValidationMatched({ row }) {
  const { dataPayload } = row.original.modelData;
  if (dataPayload?.validationSegment?.id && dataPayload?.validationSegment?.matched) {
    const { validationSegment } = dataPayload;
    const { id, matched } = validationSegment;
    return (
      <Div d="flex" align="center" justify="space-between" w="100%">
        <Text
          m={{ x: '0.25rem' }}
          data-tip={formatNumber(matched)}
          maxW="14rem"
          textWeight="400"
          textSize="small"
          style={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {formatNumber(matched)}
        </Text>
        <Link
          to={ROUTES.VIEW_SEGMENTS.replace(':entity', 'segment').replace(':entityId', id)}
          target="_blank"
          data-tip="Visualize in new tab"
        >
          <Icon name="NewWindow2" hoverTransform="scale(1.25)" size=".75rem" color="lightGrey" viewBox="0 0 12 12" />
        </Link>
      </Div>
    );
  }
  return null;
}

export function CellTag({ value, ...props }) {
  const tag = value?.dataModelType;
  const typeDetail = value?.typeDetail;
  const predictionType = value?.predictionType;
  const dataTest = value?.dataTest;
  let extraText = '';
  if (tag === 'CLASSIFIER') {
    if (predictionType === 'Early Cancellation') extraText = `${predictionType}, ${typeDetail}-day window`;
    else if (['Other', 'Premium Leakage'].includes(predictionType)) extraText = `${predictionType}, ${typeDetail}`;
    else extraText = predictionType;
  } else if (tag === 'ACTUARIAL') {
    extraText = typeDetail;
  }
  const { color, name, viewBox, tip } = getModelTagStyles(tag, extraText);
  return (
    <Tag
      p={{ x: '.375rem', y: '.188rem' }}
      h="1.125rem"
      textColor={color}
      borderColor={color}
      border="1px solid"
      data-tip={tip}
      data-test={dataTest}
      {...props}
    >
      <Icon name={name} size=".75rem" viewBox={viewBox} color={color} m={{ r: '.25rem' }} />
      <Text textSize="small">{CONSTANTS.TAL_MODEL.TAG_NAME[tag] || CONSTANTS.SEGMENT.TAG[tag]}</Text>
    </Tag>
  );
}
